import React, { useState, useEffect, useContext } from "react";
import {
  Row, Col, Card, Button, Form, InputGroup,
} from "react-bootstrap";
import {
  Search, MapPin, Droplet, AlertTriangle, Shield,
  CheckCircle2, FileText, Send, User, PhoneCall,
} from "lucide-react";
import { FaEdit, FaTrash } from "react-icons/fa";
import "../../assets/css/receiverdashboard/BloodDonation.css";
import ReceiverMasterLayout from "../../components/layout/ReceiverMasterLayout";
import AppContext from "../../context/AppContext";
import axios from "axios";
import Swal from "sweetalert2";

function BloodDonation() {
  const { baseUrl, loggedInReceiverId } = useContext(AppContext);

  const [activeTab, setActiveTab] = useState("donors");

  const [allRequests, setAllRequests]     = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [bloodGroupFilter, setBloodGroupFilter] = useState("all");

  const [myRequests, setMyRequests]   = useState([]);
  const [loading, setLoading]         = useState(false);
  const [editId, setEditId]           = useState(null);
  const [formSuccess, setFormSuccess] = useState(false);
  const [requestId, setRequestId]     = useState("");

  const [formData, setFormData] = useState({
    patientName: "", relation: "", bloodGroup: "", units: "",
    hospital: "", city: "", urgency: "", phone: "", clause: false,
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    viewAllRequests();
    viewMyRequests();
  }, []);

  const viewAllRequests = () => {
    axios.get(`${baseUrl}/receiver/blood/viewAllRequests`)
      .then((res) => {
        if (res.data.success) setAllRequests(res.data.requests || []);
      })
      .catch((err) => console.log(err));
  };

  const viewMyRequests = () => {
    axios.get(`${baseUrl}/receiver/blood/viewAllRequests`)
      .then((res) => {
        if (res.data.success) setMyRequests(res.data.requests || []);
      })
      .catch((err) => console.log(err));
  };

  const handleClaim = (req) => {
    Swal.fire({
      title: "Claim This Request?",
      html: `<b>Patient:</b> ${req.patient_name}<br/><b>Blood Group:</b> ${req.blood_group}<br/><b>Hospital:</b> ${req.hospital}`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Claim!",
      confirmButtonColor: "#dc3545",
    }).then((result) => {
      if (result.isConfirmed) {
        const fd = new FormData();
        fd.append("id", req.id);

        axios.post(`${baseUrl}/receiver/blood/claimRequest`, fd)
          .then((res) => {
            if (res.data.success) {
              Swal.fire({ title: "Claimed! 🩸", text: "Request claimed successfully!", icon: "success" });
              viewAllRequests();
            } else {
              Swal.fire({ title: "Error!", text: res.data.message || "Could not claim. Try again.", icon: "error" });
            }
          })
          .catch((err) => {
            console.log("Claim error:", err);
            Swal.fire({ title: "Error!", text: "Network error. Try again.", icon: "error" });
          });
      }
    });
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    if (formErrors[name]) setFormErrors((prev) => { const u = { ...prev }; delete u[name]; return u; });
  };

  const handleClauseToggle = () => setFormData((prev) => ({ ...prev, clause: !prev.clause }));
  const handleUrgencySelect = (value) => setFormData((prev) => ({ ...prev, urgency: value }));

  const validateForm = () => {
    const errs = {};
    if (!formData.patientName.trim()) errs.patientName = "Patient name is required";
    if (!formData.relation)           errs.relation    = "Please select a relation";
    if (!formData.bloodGroup)         errs.bloodGroup  = "Select a blood group";
    if (!formData.units || formData.units < 1 || formData.units > 20) errs.units = "Enter units between 1 and 20";
    if (!formData.hospital.trim())    errs.hospital    = "Hospital name is required";
    if (!formData.city.trim())        errs.city        = "City is required";
    if (!formData.urgency)            errs.urgency     = "Select urgency level";
    if (!formData.phone.trim())       errs.phone       = "Phone number is required";
    if (!formData.clause)             errs.clause      = "You must accept the responsibility clause";
    return errs;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const errs = validateForm();
    if (Object.keys(errs).length) { setFormErrors(errs); return; }
    if (editId) { updateRequest(); } else { createRequest(); }
  };

  const createRequest = () => {
    setLoading(true);
    const fd = new FormData();
    fd.append("receiver_id",  loggedInReceiverId);
    fd.append("patient_name", formData.patientName);
    fd.append("relation",     formData.relation);
    fd.append("blood_group",  formData.bloodGroup);
    fd.append("units",        formData.units);
    fd.append("hospital",     formData.hospital);
    fd.append("city",         formData.city);
    fd.append("urgency",      formData.urgency);
    fd.append("phone",        formData.phone);
    fd.append("clause",       formData.clause ? 1 : 0);

    axios.post(`${baseUrl}/receiver/blood/createRequest`, fd)
      .then((res) => {
        if (res.data.success) {
          setFormSuccess(true);
          setRequestId(res.data.request_id);
          viewMyRequests();
        } else {
          Swal.fire({ title: "Oops!", text: "Cannot submit request", icon: "error" });
        }
      })
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  };

  const updateRequest = () => {
    setLoading(true);
    const fd = new FormData();
    fd.append("id",           editId);
    fd.append("patient_name", formData.patientName);
    fd.append("relation",     formData.relation);
    fd.append("blood_group",  formData.bloodGroup);
    fd.append("units",        formData.units);
    fd.append("hospital",     formData.hospital);
    fd.append("city",         formData.city);
    fd.append("urgency",      formData.urgency);
    fd.append("phone",        formData.phone);
    fd.append("clause",       formData.clause ? 1 : 0);

    axios.post(`${baseUrl}/receiver/blood/updateRequest`, fd)
      .then((res) => {
        if (res.data.success) {
          Swal.fire({ title: "Updated!", text: "Request updated successfully", icon: "success" });
          viewMyRequests();
          resetForm();
        }
      })
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  };

  const deleteRequest = (id) => {
    Swal.fire({
      title: "Are You Sure?", text: "This request will be deleted!", icon: "warning",
      showCancelButton: true, confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        const fd = new FormData();
        fd.append("id", id);
        axios.post(`${baseUrl}/receiver/blood/deleteRequest`, fd)
          .then((res) => {
            if (res.data.success) {
              Swal.fire({ title: "Deleted!", text: "Request deleted", icon: "success" });
              viewMyRequests();
            }
          })
          .catch((err) => console.log(err));
      }
    });
  };

  const handleEdit = (req) => {
    setEditId(req.id);
    setFormData({
      patientName: req.patient_name, relation: req.relation,
      bloodGroup:  req.blood_group,  units:    req.units,
      hospital:    req.hospital,     city:     req.city,
      urgency:     req.urgency,      phone:    req.phone,
      clause:      req.clause == 1,
    });
    setFormSuccess(false);
    setActiveTab("request");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetForm = () => {
    setEditId(null); setFormSuccess(false); setRequestId("");
    setFormData({ patientName: "", relation: "", bloodGroup: "", units: "", hospital: "", city: "", urgency: "", phone: "", clause: false });
    setFormErrors({});
  };

  const getStatusChip = (status) => {
    const map = {
      pending:  { label: "Pending",  cls: "bd-status-pending"  },
      approved: { label: "Approved", cls: "bd-status-approved" },
      rejected: { label: "Rejected", cls: "bd-status-rejected" },
      claimed:  { label: "Claimed",  cls: "bd-status-claimed"  },
    };
    const s = map[(status || "pending").toLowerCase()] || map["pending"];
    return <span className={`bd-status-chip ${s.cls}`}>{s.label}</span>;
  };

  const getBloodGroupColor = (group) => {
    const colors = { "O-":"#dc2626","O+":"#ef4444","A-":"#f97316","A+":"#fb923c","B-":"#8b5cf6","B+":"#a78bfa","AB-":"#06b6d4","AB+":"#22d3ee" };
    return colors[group] || "#6b7280";
  };

  const renderActionButton = (req) => {
    const status = (req.status || "").toLowerCase();
    if (status === "claimed") {
      return <button className="bd-mini-btn bd-mini-btn-done" disabled>✅ Claimed</button>;
    }
    if (status === "approved") {
      return (
        <button className="bd-mini-btn bd-mini-btn-claim" onClick={() => handleClaim(req)}>
          🩸 Claim
        </button>
      );
    }
    return <span className="text-muted" style={{ fontSize: "12px" }}>—</span>;
  };

  const filteredRequests = allRequests.filter((r) => {
    const matchSearch = searchKeyword === "" ||
      (r.patient_name || "").toLowerCase().includes(searchKeyword.toLowerCase()) ||
      (r.city || "").toLowerCase().includes(searchKeyword.toLowerCase()) ||
      (r.hospital || "").toLowerCase().includes(searchKeyword.toLowerCase());
    const matchBlood = bloodGroupFilter === "all" || r.blood_group === bloodGroupFilter;
    return matchSearch && matchBlood;
  });

  return (
    <ReceiverMasterLayout>
      <div className="blood-donors-container">

        <div className="blood-page-header">
          <div className="header-text-section">
            <h1 className="header-main-title">Blood Donation</h1>
            <p className="header-sub-description">View blood requests or submit your own request</p>
          </div>
        </div>

        <div className="bd-tab-switcher mb-4">
          <button className={`bd-tab-btn ${activeTab === "donors" ? "bd-tab-active" : ""}`} onClick={() => setActiveTab("donors")}>
            <Droplet size={16} /> Blood Donors List
          </button>
          <button className={`bd-tab-btn ${activeTab === "request" ? "bd-tab-active" : ""}`} onClick={() => { setActiveTab("request"); resetForm(); }}>
            <FileText size={16} /> Blood Request Form
          </button>
        </div>

        {/* ══ BLOOD DONORS LIST TAB ══ */}
        {activeTab === "donors" && (
          <>
            <Card className="search-filters-panel mb-4">
              <Card.Body>
                <Row className="align-items-end">
                  <Col lg={4} md={6} className="mb-3 mb-lg-0">
                    <InputGroup>
                      <InputGroup.Text><Search size={18} /></InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Search by patient, city, hospital..."
                        value={searchKeyword}
                        onChange={(e) => setSearchKeyword(e.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col lg={3} md={6}>
                    <Form.Select value={bloodGroupFilter} onChange={(e) => setBloodGroupFilter(e.target.value)}>
                      <option value="all">All Blood Groups</option>
                      {["A+","A-","B+","B-","O+","O-","AB+","AB-"].map((g) => (
                        <option key={g} value={g}>{g}</option>
                      ))}
                    </Form.Select>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <div className="bd-table-card">
              <div className="bd-table-card-header bd-table-header-red">
                🩸 Blood Requests
                <span className="bd-table-count-badge">{filteredRequests.length}</span>
              </div>
              <div className="bd-table-wrap">
                <table className="bd-styled-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Patient Name</th>
                      <th>Blood Group</th>
                      <th>Units</th>
                      <th>Hospital</th>
                      <th>City</th>
                      <th>Urgency</th>
                      <th>Phone</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRequests.length === 0 ? (
                      <tr>
                        <td colSpan={10}><div className="bd-empty-row">No blood requests found.</div></td>
                      </tr>
                    ) : (
                      filteredRequests.map((req, index) => (
                        <tr key={req.id}>
                          <td>{index + 1}</td>
                          <td className="bd-cell-strong">{req.patient_name}</td>
                          <td>
                            <span className="bd-blood-chip" style={{ background: getBloodGroupColor(req.blood_group) }}>
                              <Droplet size={13} /> {req.blood_group || "—"}
                            </span>
                          </td>
                          <td>{req.units}</td>
                          <td>{req.hospital}</td>
                          <td>{req.city}</td>
                          <td>
                            <span className={`bd-urgency-chip ${req.urgency === "Emergency" ? "bd-urgency-chip-emergency" : "bd-urgency-chip-routine"}`}>
                              {req.urgency}
                            </span>
                          </td>
                          <td>{req.phone}</td>
                          <td>{getStatusChip(req.status)}</td>
                          <td>{renderActionButton(req)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ══ REQUEST FORM TAB ══ */}
        {activeTab === "request" && (
          <div className="bd-form-outer">
            {formSuccess ? (
              <div className="bd-form-card bd-success-card">
                <div className="bd-success-ring"><CheckCircle2 size={40} color="#fff" /></div>
                <h2 className="bd-success-title">Request Submitted!</h2>
                <p className="bd-success-msg">Your blood request has been recorded.</p>
                <div className="bd-success-id">Request ID: {requestId}</div>
                <button className="bd-reset-btn" onClick={resetForm}>Submit Another Request</button>
              </div>
            ) : (
              <div className="bd-form-card">
                <div className="bd-form-header">
                  <div className="bd-form-icon-ring"><Droplet size={26} color="#fff" /></div>
                  <div>
                    <h2 className="bd-form-title">Blood <span>Request</span> Form</h2>
                    <p className="bd-form-subtitle">Fill in patient details to submit a blood request.</p>
                  </div>
                </div>

                <form onSubmit={handleFormSubmit} noValidate className="bd-form-body">
                  <div className="bd-form-section">
                    <div className="bd-section-label"><User size={13} /> Patient Identity</div>
                    <Row>
                      <Col md={6} className="mb-3">
                        <label className="bd-label">Patient Name <span className="bd-req">*</span></label>
                        <input className={`bd-input${formErrors.patientName ? " bd-input-error" : ""}`} type="text" name="patientName" value={formData.patientName} onChange={handleFormChange} placeholder="Full patient name" />
                        {formErrors.patientName && <p className="bd-error">{formErrors.patientName}</p>}
                      </Col>
                      <Col md={6} className="mb-3">
                        <label className="bd-label">Relation <span className="bd-req">*</span></label>
                        <select className={`bd-select${formErrors.relation ? " bd-input-error" : ""}`} name="relation" value={formData.relation} onChange={handleFormChange}>
                          <option value="">Select relation</option>
                          {["Self","Spouse","Parent","Child","Sibling","Grandparent","Friend","Other"].map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                        {formErrors.relation && <p className="bd-error">{formErrors.relation}</p>}
                      </Col>
                    </Row>
                  </div>

                  <div className="bd-form-section">
                    <div className="bd-section-label"><Droplet size={13} /> Medical Information</div>
                    <Row>
                      <Col md={6} className="mb-3">
                        <label className="bd-label">Blood Group <span className="bd-req">*</span></label>
                        <select className={`bd-select${formErrors.bloodGroup ? " bd-input-error" : ""}`} name="bloodGroup" value={formData.bloodGroup} onChange={handleFormChange}>
                          <option value="">Select blood group</option>
                          {["A+","A-","B+","B-","O+","O-","AB+","AB-"].map((g) => <option key={g} value={g}>{g}</option>)}
                        </select>
                        {formErrors.bloodGroup && <p className="bd-error">{formErrors.bloodGroup}</p>}
                      </Col>
                      <Col md={6} className="mb-3">
                        <label className="bd-label">Units Required <span className="bd-req">*</span></label>
                        <input className={`bd-input${formErrors.units ? " bd-input-error" : ""}`} type="number" name="units" value={formData.units} onChange={handleFormChange} min="1" max="20" placeholder="e.g. 2" />
                        {formErrors.units && <p className="bd-error">{formErrors.units}</p>}
                      </Col>
                    </Row>
                  </div>

                  <div className="bd-form-section">
                    <div className="bd-section-label"><MapPin size={13} /> Location Details</div>
                    <Row>
                      <Col md={6} className="mb-3">
                        <label className="bd-label">Hospital <span className="bd-req">*</span></label>
                        <input className={`bd-input${formErrors.hospital ? " bd-input-error" : ""}`} type="text" name="hospital" value={formData.hospital} onChange={handleFormChange} placeholder="Hospital name" />
                        {formErrors.hospital && <p className="bd-error">{formErrors.hospital}</p>}
                      </Col>
                      <Col md={6} className="mb-3">
                        <label className="bd-label">City <span className="bd-req">*</span></label>
                        <input className={`bd-input${formErrors.city ? " bd-input-error" : ""}`} type="text" name="city" value={formData.city} onChange={handleFormChange} placeholder="e.g. Karachi" />
                        {formErrors.city && <p className="bd-error">{formErrors.city}</p>}
                      </Col>
                    </Row>
                  </div>

                  <div className="bd-form-section">
                    <div className="bd-section-label"><AlertTriangle size={13} /> Priority Level</div>
                    <div className="bd-urgency-grid">
                      <div className={`bd-urgency-card bd-urgency-emergency ${formData.urgency === "Emergency" ? "bd-urgency-active-emergency" : ""}`} onClick={() => handleUrgencySelect("Emergency")}>
                        <span className="bd-urgency-emoji">🚨</span>
                        <span className="bd-urgency-name">Emergency</span>
                        <span className="bd-urgency-desc">Critical — needed within hours</span>
                      </div>
                      <div className={`bd-urgency-card bd-urgency-routine ${formData.urgency === "Routine" ? "bd-urgency-active-routine" : ""}`} onClick={() => handleUrgencySelect("Routine")}>
                        <span className="bd-urgency-emoji">🗓️</span>
                        <span className="bd-urgency-name">Routine</span>
                        <span className="bd-urgency-desc">Planned — within a few days</span>
                      </div>
                    </div>
                    {formErrors.urgency && <p className="bd-error mt-2">{formErrors.urgency}</p>}
                  </div>

                  <div className="bd-form-section">
                    <div className="bd-section-label"><PhoneCall size={13} /> Contact Information</div>
                    <label className="bd-label">Mobile Number <span className="bd-req">*</span></label>
                    <div className="bd-input-wrap">
                      <span className="bd-phone-prefix">+92</span>
                      <input className={`bd-input bd-phone-input${formErrors.phone ? " bd-input-error" : ""}`} type="tel" name="phone" value={formData.phone} onChange={handleFormChange} placeholder="03XX XXXXXXX" maxLength={11} />
                    </div>
                    {formErrors.phone && <p className="bd-error">{formErrors.phone}</p>}
                  </div>

                  <div className="bd-form-section">
                    <div className="bd-section-label"><Shield size={13} /> Responsibility Clause</div>
                    <div className={`bd-clause-box ${formData.clause ? "bd-clause-checked" : ""}`} onClick={handleClauseToggle}>
                      <div className={`bd-checkbox-custom ${formData.clause ? "bd-checkbox-on" : ""}`}>
                        {formData.clause && <CheckCircle2 size={14} color="#fff" />}
                      </div>
                      <p className="bd-clause-text"><strong>I understand</strong> that I am fully responsible for the pickup and coordination of this blood donation.</p>
                    </div>
                    {formErrors.clause && <p className="bd-error mt-2">{formErrors.clause}</p>}
                  </div>

                  <div className="d-flex gap-2">
                    {editId && <button type="button" className="bd-reset-btn" onClick={resetForm}>Cancel Edit</button>}
                    <button type="submit" className={`bd-submit-btn ${loading ? "bd-submit-loading" : ""}`} disabled={loading}>
                      {loading ? <><span className="bd-spinner" /> Processing…</> : editId ? <><Send size={17} /> Update Request</> : <><Send size={17} /> Submit Blood Request</>}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* My Requests Table */}
            <div className="bd-table-card mt-5">
              <div className="bd-table-card-header bd-table-header-teal">
                My Blood Requests
                <span className="bd-table-count-badge">{myRequests.length}</span>
              </div>
              <div className="bd-table-wrap">
                <table className="bd-styled-table">
                  <thead>
                    <tr>
                      <th>#</th><th>Patient Name</th><th>Blood Group</th><th>Units</th>
                      <th>Hospital</th><th>City</th><th>Urgency</th><th>Phone</th>
                      <th>Status</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myRequests.length === 0 ? (
                      <tr><td colSpan={10}><div className="bd-empty-row">No requests found.</div></td></tr>
                    ) : (
                      myRequests.map((req, index) => (
                        <tr key={req.id}>
                          <td>{index + 1}</td>
                          <td className="bd-cell-strong">{req.patient_name}</td>
                          <td>
                            <span className="bd-blood-chip" style={{ background: getBloodGroupColor(req.blood_group) }}>
                              <Droplet size={13} /> {req.blood_group}
                            </span>
                          </td>
                          <td>{req.units}</td>
                          <td>{req.hospital}</td>
                          <td>{req.city}</td>
                          <td>
                            <span className={`bd-urgency-chip ${req.urgency === "Emergency" ? "bd-urgency-chip-emergency" : "bd-urgency-chip-routine"}`}>
                              {req.urgency}
                            </span>
                          </td>
                          <td>{req.phone}</td>
                          <td>{getStatusChip(req.status)}</td>
                          <td>
                            <div className="bd-action-group">
                              <button className="bd-icon-btn bd-icon-edit" onClick={() => handleEdit(req)}><FaEdit /></button>
                              <button className="bd-icon-btn bd-icon-delete" onClick={() => deleteRequest(req.id)}><FaTrash /></button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </ReceiverMasterLayout>
  );
}

export default BloodDonation;